<script setup lang="ts">
useHead({
  bodyAttrs: {
    class: 'bg-surface-secondary',
  },
})

definePageMeta({
  name: 'authors-index',
  layout: 'default',
  colorMode: 'light',
  theme: 'blurple',
  breadcrumb: { label: 'Forfattere' },
  title: 'Forfattere',
})
  
const { page, setPage, authors, isLoading, suspense, numFound } = useAuthorFilters()

if (import.meta.server) {
  await suspense()
}

const lastKnownTotal = ref<number>(0)
const route = useRoute<'authors-index'>()

watchEffect(() => {
  if (numFound.value) {
    lastKnownTotal.value = numFound.value
  }
})

watch(page, () => {
  if (import.meta.client) {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }
})

useTrackLastActivity(() => String(route.meta.title))
</script>

<template>
  <NuxtLayout name="overview-with-bread-crumbs-and-title" page-title="Forfattere" sticky-panel-top>
    <template #background-illustration>
      <IllustrationForfattere class="-translate-y-[8%]" />
    </template>

    <div class="w-full flex items-start gap-l">
      <StickyContent class="hidden lg:flex flex-col shrink-0 max-h-[calc(100dvh-6.5rem)] no-scrollbar overflow-y-auto [--padding-top:0rem]">
        <AuthorFilterSidebar />
      </StickyContent>

      <div class="@container flex-1 min-w-0 flex flex-col gap-2xs">
        <StickyContent class="bg-white z-10 pt-m min-[1080px]:pt-l pb-xs">
          <AuthorListHeader />
        </StickyContent>

        <h2 class="sr-only">Forfattere</h2>

        <ul class="w-full grid grid-cols-1 @[500px]:grid-cols-2 @[750px]:grid-cols-3 gap-m max-w-[959px]" aria-label="Forfattere">
          <template v-if="isLoading">
            <li v-for="index in 12" :key="index">
              <AuthorSkeleton />
            </li>
          </template>

          <template v-else-if="authors.length">
            <AuthorCard
              v-for="(author, index) in authors"
              :key="index"
              :name="author.name"
              :bio="author.bio"
              :image="author.image"
              :to="{ name: 'author', params: { slug: author.slug } }"
              class="w-full h-full block"
              :aria-label="`Lenkekort til ${author.name}`"
              orientation="vertical"
            />
          </template>

          <template v-else>
            <li class="col-span-full text-center text-text-secondary mt-xl">
              Fant ingen forfattere som matcher søket ditt.
            </li>
          </template>
        </ul>

        <div v-if="numFound > 0" class="mt-m">
          <span class="text-xs">© Illustrasjoner av Illustratør / byHands</span>
        </div>

        <div class="pt-l">
          <Pagination
            v-if="numFound > 0 && lastKnownTotal > AUTHORS_PER_PAGE"
            class="flex justify-center"
            :item-count="lastKnownTotal"
            :items-per-page="AUTHORS_PER_PAGE"
            :current-page="page"
            @go-to-page="setPage"
          />
        </div>
      </div>
    </div>
  </NuxtLayout>
</template>
